import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Loading from '../components/Loading';
import './TikTokDemoScopes.css';

function TikTokDemoScopes() {
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTikTokData();
  }, []);

  const loadTikTokData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener cuenta(s) de TikTok del usuario actual
      const response = await axios.get('/tiktok/api/tiktok-account/me/', {
        withCredentials: true
      });

      if (response.data) {
        // Verificar si es una sola cuenta o múltiples
        if (response.data.accounts) {
          // Múltiples cuentas
          setAccounts(response.data.accounts);

          // Seleccionar la cuenta principal por defecto, o la primera
          const primaryAccount = response.data.accounts.find(acc => acc.is_primary) || response.data.accounts[0];
          setSelectedAccountId(primaryAccount.id);
          setAccount(primaryAccount);
        } else {
          // Una sola cuenta (retrocompatibilidad)
          setAccount(response.data);
          setAccounts([response.data]);
          setSelectedAccountId(response.data.id);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error cargando datos de TikTok:', err);
      setError(err.response?.data?.error || 'No tienes una cuenta de TikTok conectada.');
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);

      // Llamar al endpoint que refresca los datos desde TikTok API
      await axios.post('/tiktok/api/tiktok-account/refresh/', {}, {
        withCredentials: true
      });

      // Recargar datos
      await loadTikTokData();
    } catch (err) {
      console.error('Error refrescando datos:', err);
      alert('Error al refrescar datos: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const handleAccountChange = (accountId) => {
    const newAccount = accounts.find(acc => acc.id === parseInt(accountId));
    if (newAccount) {
      setSelectedAccountId(newAccount.id);
      setAccount(newAccount);
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await axios.post(`/tiktok/api/tiktok-account/${accountId}/set-primary/`, {}, {
        withCredentials: true
      });

      // Recargar datos
      await loadTikTokData();
      alert('Cuenta principal actualizada');
    } catch (err) {
      console.error('Error estableciendo cuenta principal:', err);
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDisconnect = async (accountId, username) => {
    if (!window.confirm(`¿Estás seguro que deseas desconectar @${username}?`)) {
      return;
    }

    try {
      await axios.delete(`/tiktok/api/tiktok-account/${accountId}/disconnect/`, {
        withCredentials: true
      });

      alert('Cuenta desconectada exitosamente');

      // Recargar datos
      await loadTikTokData();
    } catch (err) {
      console.error('Error desconectando cuenta:', err);
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleConnectTikTok = () => {
    // EXACTAMENTE igual que en InfluencerModal - esto SÍ funciona
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // IMPORTANTE: Detectar si estamos en calendario.geves.cl
    let backendUrl;
    if (window.location.hostname === 'calendario.geves.cl') {
      // Producción - usar calendario.geves.cl directamente
      backendUrl = 'https://calendario.geves.cl';
    } else {
      // Local o desarrollo - extraer del apiUrl
      backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');
    }

    const connectUrl = `${backendUrl}/tiktok/connect/`;

    console.log('🔗 Abriendo ventana de TikTok:', connectUrl);

    // Abrir popup EXACTAMENTE como Influencers (esto SÍ funciona)
    window.open(connectUrl, 'TikTokAuth', 'width=600,height=700');
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="tiktok-demo-container">
        <h1>📱 TikTok Integration Dashboard</h1>
        <div className="error-box">
          <h3>⚠️ {error}</h3>
          <p style={{ marginTop: '15px', marginBottom: '20px' }}>
            No tienes una cuenta de TikTok conectada. Conecta tu cuenta para ver tus datos y publicar videos.
          </p>
          <button
            onClick={handleConnectTikTok}
            className="btn-refresh"
            style={{ marginRight: '10px' }}
          >
            🔗 Conectar TikTok
          </button>
          <a
            href="/"
            className="btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-block' }}
          >
            ← Volver al inicio
          </a>
        </div>

        <div className="card" style={{ marginTop: '30px' }}>
          <h2>ℹ️ ¿Qué sucederá al conectar?</h2>
          <p style={{ marginBottom: '15px' }}>
            Al hacer clic en "Conectar TikTok", serás redirigido a TikTok para autorizar nuestra aplicación.
          </p>
          <p style={{ marginBottom: '15px' }}>
            <strong>Scopes que se solicitarán:</strong>
          </p>
          <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
            <li><strong>user.info.profile</strong> - Tu nombre de usuario y foto de perfil</li>
            <li><strong>user.info.stats</strong> - Estadísticas de tu cuenta (seguidores, likes, videos)</li>
            <li><strong>video.list</strong> - Listar tus videos publicados</li>
            <li><strong>video.upload</strong> - Subir videos a TikTok</li>
            <li><strong>video.publish</strong> - Publicar videos en tu cuenta</li>
          </ul>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Asegúrate de <strong>autorizar todos los permisos</strong> cuando TikTok te lo solicite para poder usar todas las funcionalidades.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tiktok-demo-container">
      <div className="header-section">
        <div>
          <h1>📱 TikTok Integration Dashboard</h1>
          {accounts.length > 1 && (
            <div style={{ marginTop: '10px' }}>
              <label htmlFor="account-selector" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                Cuenta:
              </label>
              <select
                id="account-selector"
                value={selectedAccountId || ''}
                onChange={(e) => handleAccountChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  marginRight: '10px'
                }}
              >
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    @{acc.username} {acc.is_primary ? '⭐ (Principal)' : ''}
                  </option>
                ))}
              </select>
              {!account?.is_primary && (
                <button
                  onClick={() => handleSetPrimary(selectedAccountId)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginRight: '10px'
                  }}
                >
                  ⭐ Marcar como principal
                </button>
              )}
              {accounts.length > 1 && (
                <button
                  onClick={() => handleDisconnect(selectedAccountId, account.username)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#c33'
                  }}
                >
                  ❌ Desconectar cuenta
                </button>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <button onClick={refreshData} className="btn-refresh">
            🔄 Actualizar Datos
          </button>
          <button
            onClick={handleConnectTikTok}
            className="btn-refresh"
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
            }}
          >
            ➕ Agregar otra cuenta
          </button>
        </div>
      </div>

      {/* USER.INFO.PROFILE */}
      <div className="card">
        <h2>
          👤 Información del Perfil
          <span className="scope-badge">user.info.profile</span>
        </h2>
        <div className="profile-section">
          {account.avatar_url ? (
            <img src={account.avatar_url} alt={account.username} className="avatar" />
          ) : (
            <div className="avatar-placeholder"></div>
          )}
          <div className="profile-info">
            <h3>@{account.username}</h3>
            <p><strong>Nombre:</strong> {account.display_name || 'N/A'}</p>
            <p><strong>User ID:</strong> {account.tiktok_user_id}</p>
            <p><strong>Conectada:</strong> {new Date(account.connected_at).toLocaleDateString('es-ES')}</p>
          </div>
        </div>
      </div>

      {/* USER.INFO.STATS */}
      <div className="card">
        <h2>
          📊 Estadísticas de la Cuenta
          <span className="scope-badge">user.info.stats</span>
        </h2>
        <div className="stats-grid">
          <div className="stat-box">
            <div className="number">{account.followers_count?.toLocaleString() || '0'}</div>
            <div className="label">Seguidores</div>
          </div>
          <div className="stat-box">
            <div className="number">{account.following_count?.toLocaleString() || '0'}</div>
            <div className="label">Seguidos</div>
          </div>
          <div className="stat-box">
            <div className="number">{account.likes_count?.toLocaleString() || '0'}</div>
            <div className="label">Likes</div>
          </div>
          <div className="stat-box">
            <div className="number">{account.video_count?.toLocaleString() || '0'}</div>
            <div className="label">Videos</div>
          </div>
        </div>
      </div>

      {/* VIDEO.LIST */}
      <div className="card">
        <h2>
          🎬 Videos Publicados
          <span className="scope-badge">video.list</span>
        </h2>

        {videos.length > 0 ? (
          <div className="videos-grid">
            {videos.map((video, index) => (
              <div key={index} className="video-card">
                {video.cover_image_url ? (
                  <img src={video.cover_image_url} alt="Video thumbnail" className="video-thumbnail" />
                ) : (
                  <div className="video-thumbnail-placeholder"></div>
                )}
                <div className="video-info">
                  <div className="title">
                    {video.title || video.video_description || 'Sin título'}
                  </div>
                  <div className="video-stats">
                    <span>👁️ {video.view_count?.toLocaleString() || '0'}</span>
                    <span>❤️ {video.like_count?.toLocaleString() || '0'}</span>
                    <span>💬 {video.comment_count?.toLocaleString() || '0'}</span>
                  </div>
                  <p className="video-date">
                    {video.create_time ? new Date(video.create_time).toLocaleDateString('es-ES') : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No se encontraron videos o aún no se ha sincronizado la lista.</p>
            <button onClick={refreshData} className="btn-secondary">
              Actualizar lista de videos
            </button>
          </div>
        )}
      </div>

      {/* CONTENT POSTING API INFO */}
      <div className="card">
        <h2>
          📤 Content Posting API
          <span className="scope-badge">video.upload</span>
          <span className="scope-badge">video.publish</span>
        </h2>
        <div className="success-box">
          <p><strong>✅ Funcionalidad Activa</strong></p>
          <p style={{ marginTop: '10px' }}>
            Esta cuenta puede publicar videos en TikTok usando Content Posting API.
            Los scopes <code>video.upload</code> y <code>video.publish</code> están activos.
          </p>
          <p style={{ marginTop: '10px' }}>
            Para publicar un video, ve al <strong>Calendario de Contenido</strong> y selecciona un post aprobado.
          </p>
        </div>
      </div>

      <div className="footer-section">
        <a href="/" className="btn-back">← Volver al Dashboard</a>
      </div>
    </div>
  );
}

export default TikTokDemoScopes;
